/* eslint-disable max-lines */
import axios, { AxiosRequestConfig } from "axios";
import { API_URL, ENV } from "../config";
import {
  z,
  ZodArray,
  ZodBoolean,
  ZodCatch,
  ZodDefault,
  ZodDiscriminatedUnion,
  ZodEffects,
  ZodNullable,
  ZodObject,
  ZodOptional,
  ZodRecord,
  ZodTuple,
  ZodTypeAny,
  ZodUnion,
} from "zod";
import { duplicateCallTracker } from "./duplicateCallTracker";
import { RequestCanceledError } from "./requestCanceledError";
import { ApiRequestError } from "./ApiRequestError";
import * as Sentry from "@sentry/react";

// Type for accepted Zod schemas in API methods
type AcceptedZodSchema =
  | ZodObject<z.ZodRawShape>
  | ZodArray<ZodTypeAny>
  | ZodUnion<[ZodTypeAny, ...ZodTypeAny[]]>
  | ZodDiscriminatedUnion<string, [ZodObject<z.ZodRawShape>, ...ZodObject<z.ZodRawShape>[]]>
  | ZodRecord<z.ZodString, ZodTypeAny>
  | ZodBoolean;

axios.defaults.baseURL = API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json";

// Legacy callers receive this 404 string prefix; opted-in requests retain
// their HTTP status in ApiRequestError. Legacy consumers use startsWith so
// a phrase elsewhere in a server detail is not mistaken for a 404.
export const NOT_FOUND_MESSAGE_PREFIX = "Sorry! the data you are looking for could not be found.";

/**
 * Renders a FastAPI `detail` payload as user-facing text, or `undefined`
 * when it holds nothing a person can read.
 *
 * `detail` is not always a string. The `/api/v2` endpoints reject with a
 * coded object (`{code: "APPS.FILTER_REQUIRED", message: "..."}`) and
 * request-validation failures reject with an array of `{msg, loc, type}`.
 * Interpolating either straight into a template literal renders the
 * infamous "[object Object]", which tells the user nothing — so callers
 * must omit the detail clause entirely rather than print that.
 */
export const readableDetail = (detail: unknown): string | undefined => {
  if (typeof detail === "string") {
    return detail.trim() || undefined;
  }
  if (Array.isArray(detail)) {
    const parts = detail.map(readableDetail).filter(Boolean);
    return parts.length ? parts.join("; ") : undefined;
  }
  if (detail && typeof detail === "object") {
    const record = detail as Record<string, unknown>;
    // `message` is the v2 coded-error field; `msg` is FastAPI's validation
    // field. `code` is the last resort — an identifier is still more use to
    // a user reporting a problem than "[object Object]".
    for (const key of ["message", "msg", "detail", "code"]) {
      const value = record[key];
      if (typeof value === "string" && value.trim()) {
        return value.trim();
      }
    }
  }
  return undefined;
};

const streamHeaders: Record<string, string> = {};

// intercepting to capture errors
axios.interceptors.response.use(
  function (response) {
    return response.data !== undefined ? response.data : response;
  },
  async function (error) {
    if (axios.isCancel(error)) {
      return Promise.reject(new RequestCanceledError());
    }
    // CSV exports receive JSON error bodies as Blobs. Decode only opted-in
    // failures so their code/message survive without changing legacy callers.
    let detail = error.response?.data?.detail;
    const data = error.response?.data;
    if (
      error.config?.preserveErrorStatus &&
      error.config?.responseType === "blob" &&
      (data instanceof Blob || typeof data === "string")
    ) {
      try {
        const text = typeof data === "string" ? data : await data.text();
        detail = JSON.parse(text)?.detail;
      } catch {
        // Non-JSON error pages still retain their HTTP status below.
      }
    }
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    let message;
    const status = error.response?.status;
    switch (status) {
      case 500:
        message = detail || "Internal Server Error";
        break;
      case 401:
        message = detail || "Invalid credentials";
        break;
      case 404: {
        // Only append the detail when it renders as text. A coded object or
        // a validation array has no string form worth showing, so the whole
        // "Details:" clause is dropped rather than printed as
        // "[object Object]".
        const readable = readableDetail(detail);
        message = readable
          ? `${NOT_FOUND_MESSAGE_PREFIX} Details: ${readable}`
          : NOT_FOUND_MESSAGE_PREFIX;
        break;
      }
      default:
        message = detail || error?.message || error;
    }

    // Send API errors to Rollbar if enabled
    if (window.Rollbar && status && status >= 500) {
      window.Rollbar.error("API Error", {
        url: error.config?.url,
        method: error.config?.method,
        status: status,
        statusText: error.response?.statusText,
        message: message,
        response: error.response?.data,
      });
    }

    if (error.config?.preserveErrorStatus) {
      return Promise.reject(
        new ApiRequestError(
          readableDetail(message) || "Request failed",
          status,
          typeof detail?.code === "string" ? detail.code : undefined
        )
      );
    }
    return Promise.reject(message);
  }
);

/** The team switcher's "Default" option — an account-wide view rather than a
 *  real team, so it deliberately sends no header. Exported because callers
 *  that decide whether a team is selected must agree with this. */
export const NO_USER_GROUP_ID = "-1";

const setUserGroupHeader = (userGroupId?: string) => {
  console.log("Setting team header...", userGroupId);
  if (userGroupId && userGroupId !== NO_USER_GROUP_ID) {
    axios.defaults.headers.common["x-user-group-id"] = userGroupId;
    streamHeaders["x-user-group-id"] = userGroupId;
  } else {
    delete axios.defaults.headers.common["x-user-group-id"];
    delete streamHeaders["x-user-group-id"];
  }
};

// Initialize the team header from localStorage at module load so the VERY FIRST
// request is RBAC-scoped to the selected team. UserGroupSelector also sets it
// reactively, but that runs only after the team list finishes loading, which
// races with early data fetches (and a stale/absent header leaks another team's
// data until the refetch). The key matches UserGroupSelector's LocalStorageKey.
try {
  setUserGroupHeader(localStorage.getItem("userGroupId") ?? undefined);
} catch {
  // localStorage unavailable (SSR / privacy mode) — header stays unset.
}

const setAuthorizationHeader = (authToken: string) => {
  console.log("Setting authorization header...");
  if (authToken) {
    axios.defaults.headers.common["Authorization"] = "Bearer " + authToken;
    streamHeaders["Authorization"] = "Bearer " + authToken;
  } else {
    delete axios.defaults.headers.common["Authorization"];
    delete streamHeaders["Authorization"];
  }
};

const setTenantHeader = (tenant: string) => {
  console.log("Setting tenant...");
  if (tenant) {
    axios.defaults.headers.common["x-tenant"] = tenant;
    streamHeaders["x-tenant"] = tenant;
  } else {
    delete axios.defaults.headers.common["x-tenant"];
    delete streamHeaders["x-tenant"];
  }
};

const checkSecureConnection = () => {
  if (ENV === "staging" || ENV === "production") {
    if (window.location.protocol !== "https:" || API_URL?.substring(0, 5) !== "https") {
      return false;
    }
  }
  return true;
};

const applyCatchToSchema = <T extends ZodTypeAny>(schema: T, defaultValue = "test"): T => {
  if (schema instanceof ZodObject) {
    const shape = schema.shape; // Access the shape correctly
    const newShape = Object.fromEntries(
      Object.entries(shape).map(([key, value]) => [
        key,
        applyCatchToSchema(value as ZodTypeAny, defaultValue),
      ])
    );
    return new ZodObject({
      ...schema._def,
      shape: () => newShape,
    }) as unknown as T;
  } else if (schema instanceof ZodArray) {
    // Guarantee an array reaches consumers: a non-array response (empty body,
    // error payload) coerces to [] instead of letting a raw non-array crash a
    // downstream .map(). A real array whose elements fail deep validation is
    // kept as-is rather than nuked to [], so partially-valid rows still render.
    return new ZodArray({
      ...schema._def,
      type: applyCatchToSchema(schema._def.type, defaultValue),
    }).catch(ctx => (Array.isArray(ctx.input) ? ctx.input : [])) as unknown as T;
  } else if (schema instanceof ZodNullable || schema instanceof ZodDefault) {
    return schema.catch("") as unknown as T;
  } else if (schema._def.typeName === "ZodString" || schema._def.typeName === "ZodNumber") {
    schema._def.defaultValue = undefined;
    // Applies .catch to string and number schemas
    return schema.catch("") as unknown as T;
  }

  return schema;
};

// The backend serialises an absent value as `null`, while many FE schemas
// declare it `.optional()` (undefined only). Treat that null as absent,
// literally: before validation every plain ZodOptional maps `null` to
// `undefined`, so the mismatch is neither reported as drift nor sent through
// recovery, `.optional().default(x)` yields `x`, and pages never receive
// `null` for an optional field. Because null never reaches the parsed output,
// `z.infer` of the original schema (`T | undefined`) stays accurate, which is
// why widenOptionalToNullish can return the input schema's type.
//
// Maintainer policy note: .claude/rules/guidelines/typescript.md says "If the
// backend can omit or null a field ..., the Zod schema must say so". This is a
// deliberate global runtime policy on top of that rule, chosen to stop
// null-for-optional drift noise in Sentry (AI-9716); schemas that need to keep
// `null` as a distinct value must still declare `.nullable()` / `.nullish()`,
// and those are left untouched here. Required fields stay strict.
//
// Memoised per schema instance; shared sub-schemas are rebuilt once.
const widenedSchemas = new WeakMap<ZodTypeAny, ZodTypeAny>();

const nullToUndefined = (value: unknown) => (value === null ? undefined : value);

const widen = (schema: ZodTypeAny): ZodTypeAny => {
  const cached = widenedSchemas.get(schema);
  if (cached) return cached;

  let result: ZodTypeAny = schema;
  if (schema instanceof ZodOptional) {
    const optional = new ZodOptional({ ...schema._def, innerType: widen(schema._def.innerType) });
    // `.nullable().optional()` / `.nullish()` already declare null: keep it.
    result =
      schema._def.innerType instanceof ZodNullable
        ? optional
        : z.preprocess(nullToUndefined, optional);
  } else if (schema instanceof ZodDefault && schema._def.innerType instanceof ZodOptional) {
    // `.optional().default(x)`: map null before ZodDefault so x applies.
    const withDefault = new ZodDefault({
      ...schema._def,
      innerType: widen(schema._def.innerType),
    });
    result =
      schema._def.innerType._def.innerType instanceof ZodNullable
        ? withDefault
        : z.preprocess(nullToUndefined, withDefault);
  } else if (schema instanceof ZodObject) {
    const newShape = Object.fromEntries(
      Object.entries(schema.shape as z.ZodRawShape).map(([key, value]) => [key, widen(value)])
    );
    // Spreading _def keeps unknownKeys (strip/passthrough/strict) and catchall.
    result = new ZodObject({ ...schema._def, shape: () => newShape });
  } else if (schema instanceof ZodArray) {
    result = new ZodArray({ ...schema._def, type: widen(schema._def.type) });
  } else if (
    schema instanceof ZodNullable ||
    schema instanceof ZodDefault ||
    schema instanceof ZodCatch
  ) {
    result = new (schema.constructor as new (def: unknown) => ZodTypeAny)({
      ...schema._def,
      innerType: widen(schema._def.innerType),
    });
  } else if (schema instanceof ZodDiscriminatedUnion) {
    result = z.discriminatedUnion(
      schema._def.discriminator,
      schema._def.options.map(widen) as [ZodObject<z.ZodRawShape>, ...ZodObject<z.ZodRawShape>[]],
      { ...schema._def }
    );
  } else if (schema instanceof ZodUnion) {
    result = new ZodUnion({ ...schema._def, options: schema._def.options.map(widen) });
  } else if (schema instanceof ZodRecord) {
    result = new ZodRecord({ ...schema._def, valueType: widen(schema._def.valueType) });
  } else if (schema instanceof ZodEffects) {
    result = new ZodEffects({ ...schema._def, schema: widen(schema._def.schema) });
  } else if (schema instanceof ZodTuple) {
    result = new ZodTuple({
      ...schema._def,
      items: schema._def.items.map(widen),
      rest: schema._def.rest ? widen(schema._def.rest) : schema._def.rest,
    });
  }

  widenedSchemas.set(schema, result);
  return result;
};

export const widenOptionalToNullish = <T extends ZodTypeAny>(schema: T): T =>
  widen(schema) as unknown as T;

const detectUnknownFields = (
  schema: z.ZodObject<z.ZodRawShape>,
  data: unknown,
  endpoint: string
) => {
  if (typeof data !== "object" || data === null) {
    return;
  }

  // Get all defined keys in the schema
  const schemaKeys = new Set(Object.keys(schema.shape));

  // Find keys in response that are not in schema
  const dataRecord = data as Record<string, unknown>;
  const unknownKeys = Object.keys(dataRecord).filter(key => !schemaKeys.has(key));

  if (unknownKeys.length > 0) {
    console.error(`[Zod Passthrough Warning] Unknown fields detected in API response:`, {
      endpoint,
      unknownFields: unknownKeys,
      values: unknownKeys.reduce(
        (acc, key) => {
          acc[key] = dataRecord[key];
          return acc;
        },
        {} as Record<string, unknown>
      ),
    });
  }

  // Recursively check nested objects
  Object.entries(schema.shape).forEach(([key, fieldSchema]) => {
    const fieldData = dataRecord[key];
    if (fieldData === undefined || fieldData === null) {
      return;
    }

    // Unwrap ZodOptional, ZodNullable, ZodDefault to get the inner schema
    let unwrappedSchema = fieldSchema as ZodTypeAny;
    while (
      unwrappedSchema._def.typeName === "ZodOptional" ||
      unwrappedSchema._def.typeName === "ZodNullable" ||
      unwrappedSchema._def.typeName === "ZodDefault"
    ) {
      unwrappedSchema = unwrappedSchema._def.innerType || unwrappedSchema._def.type;
    }

    // Check nested objects
    if (unwrappedSchema instanceof ZodObject) {
      detectUnknownFields(unwrappedSchema, fieldData, `${endpoint}.${key}`);
    }
    // Check arrays of objects
    else if (unwrappedSchema instanceof ZodArray) {
      const arrayItemSchema = unwrappedSchema._def.type;
      if (arrayItemSchema instanceof ZodObject && Array.isArray(fieldData)) {
        fieldData.forEach((item: unknown, index: number) => {
          detectUnknownFields(arrayItemSchema, item, `${endpoint}.${key}[${index}]`);
        });
      }
    }
    // Check union types
    else if (unwrappedSchema instanceof ZodUnion) {
      // For unions, try to find a matching object schema
      const objectSchemas = unwrappedSchema._def.options.filter(
        (option: ZodTypeAny) => option instanceof ZodObject
      );
      if (objectSchemas.length > 0 && typeof fieldData === "object") {
        // Check against the first matching object schema
        // (ideally we'd check which variant matches, but this is a reasonable approximation)
        detectUnknownFields(objectSchemas[0], fieldData, `${endpoint}.${key}`);
      }
    }
  });
};

// Collapse per-resource identifiers (numeric ids, UUIDs, long hex/hash tokens,
// cluster ids like 0622-195651-4l8m8iz6) to `:id` so `/query/v2/sql_lineage/<id>`
// and `/alerts/rules/<id>` group as one Sentry issue instead of one per id.
const normalizeEndpointForGrouping = (endpoint: string): string =>
  endpoint
    .split("/")
    .map(seg =>
      /^\d+$/.test(seg) ||
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg) ||
      /^[0-9a-f]{16,}$/i.test(seg) ||
      /^\d{4}-\d{6}-[0-9a-z]+$/i.test(seg)
        ? ":id"
        : seg
    )
    .join("/");

const patchWithZodSchema = <R>(
  response: unknown,
  endpoint: string,
  zodSchema: AcceptedZodSchema
) => {
  if (!zodSchema) return response as R;
  try {
    // Validate against the null-tolerant copy; detectUnknownFields and the
    // recovery parse below keep the original schema (same keys; recovery
    // output for real drift stays exactly as before).
    const validationResult = widenOptionalToNullish(zodSchema).safeParse(response);
    if (validationResult.success) {
      // Detect unknown fields if schema is a ZodObject with passthrough
      if (zodSchema instanceof ZodObject) {
        detectUnknownFields(zodSchema, response, endpoint);
      } else if (zodSchema instanceof ZodArray) {
        // For arrays, check each item if the array schema is an object
        const arrayItemSchema = zodSchema._def.type;
        if (arrayItemSchema instanceof ZodObject && Array.isArray(response)) {
          response.forEach((item: unknown, index: number) => {
            detectUnknownFields(arrayItemSchema, item, `${endpoint}[${index}]`);
          });
        }
      }

      return validationResult.data as R;
    }

    // A failed parse is only worth reporting when the payload is structurally
    // the kind of thing the schema describes but has drifted fields. A body
    // that cannot match the schema's container at all — an empty/204 body, an
    // error payload, or the raw AxiosResponse the interceptor forwards when
    // `response.data` is undefined — is a transport artifact, not schema
    // drift. Those recover silently (arrays -> [], objects -> caught fields)
    // instead of flooding Sentry with unactionable "Zod validation error"
    // noise, while a real field-level mismatch on a correctly-shaped payload
    // is still captured.
    const isTransportArtifact =
      response === null ||
      response === undefined ||
      response === "" ||
      (zodSchema instanceof ZodArray && !Array.isArray(response)) ||
      ((zodSchema instanceof ZodObject || zodSchema instanceof ZodRecord) &&
        (typeof response !== "object" || Array.isArray(response))) ||
      (zodSchema instanceof ZodBoolean && typeof response !== "boolean");

    if (!isTransportArtifact) {
      // Log to console
      console.error("zod validation error", endpoint, validationResult.error, response);

      // Build a specific, per-endpoint error so Sentry stops grouping every
      // schema drift under one generic "Zod validation error". The message
      // names the endpoint and the drifted field paths; the fingerprint keys
      // on the id-normalized endpoint + fields so each distinct drift is its
      // own actionable issue instead of a single opaque bucket.
      const normalizedEndpoint = normalizeEndpointForGrouping(endpoint);
      // Drop array indices from the path so per-row drift ("0.status", "1.status")
      // groups under one field ("status") instead of a distinct issue per row.
      // Full paths are preserved in extra.validationErrors below.
      const fieldPaths = Array.from(
        new Set(
          validationResult.error.errors.map(
            issue => issue.path.filter(seg => typeof seg !== "number").join(".") || "(root)"
          )
        )
      );

      Sentry.captureException(
        new Error(`Zod validation failed: ${normalizedEndpoint} [${fieldPaths.join(", ")}]`),
        {
          level: "error",
          fingerprint: ["zod-validation", normalizedEndpoint, ...fieldPaths],
          tags: {
            type: "zod_validation",
            endpoint: normalizedEndpoint,
          },
          extra: {
            endpoint: endpoint,
            fieldPaths: fieldPaths,
            validationErrors: validationResult.error.errors,
            response: response,
            zodErrorMessage: validationResult.error.message,
          },
        }
      );
    }

    try {
      return applyCatchToSchema(zodSchema).parse(response) as R;
    } catch {
      // The recovery parse can still throw (applyCatchToSchema leaves booleans,
      // enums, records and required nested objects unwrapped). Whether the body
      // was a transport artifact (never captured) or genuine drift (already
      // captured above with endpoint and field context), the failure has been
      // judged once: pass the raw body through so the outer catch does not
      // report the same ZodError a second time as `zod_schema_patch_error`.
      return response as R;
    }
  } catch (e) {
    console.error("patchWithZodSchema", endpoint, e, response);

    // Capture schema patching error in Sentry
    Sentry.captureException(e instanceof Error ? e : new Error(String(e)), {
      level: "error",
      tags: {
        type: "zod_schema_patch_error",
        endpoint: endpoint,
      },
      extra: {
        endpoint: endpoint,
        response: response,
        error: e,
      },
    });

    return response as R;
  }
};

class APIClient {
  controller?: AbortController;
  /**
   * Fetches data from given url
   */

  constructor() {
    if (!AbortSignal.timeout) {
      AbortSignal.timeout = (ms: number) => {
        const controller = new AbortController();
        setTimeout(() => controller.abort(), ms);
        return controller.signal;
      };
    }
  }

  get = async <R>(
    url: string,
    params: unknown,
    timeout = 0,
    zodSchema: AcceptedZodSchema,
    config?: AxiosRequestConfig & { preserveErrorStatus?: boolean }
  ) => {
    // Track duplicate API calls
    duplicateCallTracker.trackCall(url, "GET", params);

    if (!checkSecureConnection()) {
      return;
    }
    this.controller = new AbortController();
    const defaultConfig: AxiosRequestConfig = {
      signal: this.controller.signal,
    };
    if (timeout) {
      defaultConfig.signal = AbortSignal.timeout(timeout);
    }
    const finalConfig: AxiosRequestConfig = {
      ...defaultConfig,
      ...config,
      headers: {
        ...axios.defaults.headers.common, // Include global headers
        ...(config?.headers || {}), // Overwrite with explicitly passed headers
      },
    };
    // Empty params keep the raw passthrough they always had (no schema strip),
    // so callers still receive fields the FE schema doesn't model. The one
    // exception is an array-typed endpoint: an empty/short body surfaces here
    // as a non-array (the raw AxiosResponse) that would crash a downstream
    // .map(), so report it through the validation path and recover with [].
    if (!params || !Object.keys(params).length) {
      const result = await axios.get<R, R>(`${url}`, finalConfig);
      return zodSchema instanceof ZodArray && !Array.isArray(result)
        ? patchWithZodSchema<R>(result, url, zodSchema)
        : result;
    }
    const paramKeys = [];
    for (const k in params) {
      const v = (params as Record<string, string | number | boolean>)[k];
      // Skip undefined and null values
      if (v === undefined || v === null) {
        continue;
      }
      if (!Array.isArray(v)) {
        paramKeys.push(`${k}=${encodeURIComponent(v)}`);
        continue;
      }
      for (const i of v) {
        paramKeys.push(`${k}=${encodeURIComponent(i)}`);
      }
    }

    return patchWithZodSchema<R>(
      await axios.get<R>(`${url}?${paramKeys.join("&")}`, finalConfig),
      url,
      zodSchema
    );
  };
  /**
   * post given data to url
   */
  post = async <R, D = unknown>(
    url: string,
    data: D,
    zodSchema: AcceptedZodSchema,
    config?: AxiosRequestConfig
  ): Promise<R | undefined> => {
    if (!checkSecureConnection()) {
      return;
    }
    this.controller = new AbortController();
    return patchWithZodSchema<R>(
      await axios.post(url, data, {
        ...config,
        signal: this.controller.signal,
      }),
      url,
      zodSchema
    );
  };

  postWithParams = async <R, P = Record<string, unknown>, D = Record<string, unknown>>(
    url: string,
    params: P,
    data: D,
    zodSchema: AcceptedZodSchema
  ) => {
    if (!checkSecureConnection()) {
      return;
    }
    this.controller = new AbortController();
    return patchWithZodSchema<R>(
      await axios.post(url, data, { params, signal: this.controller.signal }),
      url,
      zodSchema
    );
  };
  /**
   * Updates data
   */
  update = async <R, D = Record<string, unknown>>(
    url: string,
    data: D,
    zodSchema: AcceptedZodSchema
  ) => {
    if (!checkSecureConnection()) {
      return;
    }
    return patchWithZodSchema<R>(
      await axios.patch(url, data, { signal: this.controller?.signal }),
      url,
      zodSchema
    );
  };

  put = async <R, D = Record<string, unknown>>(
    url: string,
    data: D,
    zodSchema: AcceptedZodSchema
  ) => {
    if (!checkSecureConnection()) {
      return;
    }
    return patchWithZodSchema<R>(
      await axios.put(url, data, { signal: this.controller?.signal }),
      url,
      zodSchema
    );
  };
  /**
   * Delete
   */
  delete = async <R>(
    url: string,
    config: Record<string, unknown>,
    zodSchema: AcceptedZodSchema
  ) => {
    if (!checkSecureConnection()) {
      return;
    }
    return patchWithZodSchema<R>(
      await axios.delete(url, { ...config, signal: this.controller?.signal }),
      url,
      zodSchema
    );
  };

  cancel = () => {
    this.controller?.abort();
  };
}

async function* getIterableStream(body: ReadableStream<Uint8Array>) {
  const reader = body.getReader();
  const decoder = new TextDecoder();

  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }
    const decodedChunk = decoder.decode(value, { stream: true });
    yield decodedChunk;
  }
}

const generateStream = async (
  url: string,
  onProgress: (chunk: string) => void,
  requestBody: Record<string, unknown> | null = null,
  abortController: AbortController | undefined = undefined
) => {
  const requestData: {
    method: "GET" | "POST";
    headers: Record<string, string>;
    body?: string;
    signal?: AbortSignal;
  } = { method: "GET", headers: streamHeaders };
  if (requestBody) {
    requestData.method = "POST";
    requestData.headers["Content-Type"] = "application/json";
    requestData.body = JSON.stringify(requestBody);
  }

  if (abortController) {
    requestData.signal = abortController.signal;
  }

  const response = await fetch(API_URL + url, requestData);
  if (response.status !== 200) throw new Error(response.status.toString());
  if (!response.body) throw new Error("Response body does not exist");

  const stream = await getIterableStream(response.body);
  for await (const chunk of stream) {
    onProgress(chunk);
  }
};

export {
  axios,
  APIClient,
  setAuthorizationHeader,
  setUserGroupHeader,
  setTenantHeader,
  getIterableStream,
  generateStream,
  duplicateCallTracker,
  streamHeaders,
};

export const api = new APIClient();

/**
 * Validate a response against its schema the way parameterised GETs do: drift
 * is reported (per endpoint and field) and recovered from the schema's catches.
 * For GETs without params, which skip validation to keep unmodelled fields.
 */
export const validateResponse = <R>(
  response: unknown,
  endpoint: string,
  zodSchema: AcceptedZodSchema
): R => patchWithZodSchema<R>(response, endpoint, zodSchema) as R;
